/* eslint-disable no-shadow */
/* eslint-disable no-multi-assign */

import { defaults, isEqual } from "lodash";
import { useRef, useCallback, Dispatch, SetStateAction } from "react";
import useRender from "./useRender";

function compare(value1: unknown, value2: unknown, deep = false) {
	return deep ? isEqual(value1, value2) : value1 === value2;
}

interface Options {
	deep?: boolean;
	when?: () => boolean;
}

export default function useInternal<T>(value: T, options: Options = {}) {
	const render = useRender();

	const internalValue = useRef(value);
	const originalValue = useRef(value);

	const { deep, when } = defaults(options, { deep: true });

	const setInternalValue = useCallback(
		(next: SetStateAction<T>) => {
			let v = next;
			if (typeof v === "function")
				v = (v as (prevState: T) => T)(internalValue.current);

			if (compare(internalValue.current, v, deep)) return;

			internalValue.current = v;

			render(true);
		},
		[internalValue, render, deep],
	);

	const use = when?.() ?? true;

	if (!compare(originalValue.current, value, deep))
		internalValue.current = originalValue.current = value;

	return [use ? internalValue.current : value, setInternalValue] as [
		T,
		Dispatch<SetStateAction<T>>,
	];
}
